import { store } from '../store'
// utils

import { ContractType, LegalEntityParty, Message as Response } from '@types'
import {
  addNewUserDetails,
  amendUserDetails,
  getAllAdminUsers
} from 'redux/slices/admin-users'
import {
  appendGuaranteeSuccess,
  appendGuaranteeSuccessForWorkflow,
  appendPaperGuaranteeSuccess
} from 'redux/slices/all-guarantees'
import { setHistory } from 'redux/slices/contract-history'
import { clearContractHistory } from 'redux/slices/contract-history'
import { getContractPdf } from 'redux/slices/contractPdf'
import {
  getGuaranteeRequestSuccess,
  startGetGuaranteeLoading,
  stopGetGuaranteeLoading,
  clearGuaranteeRequest
} from 'redux/slices/create-guarantee-request'
import {
  hasCreatePayloadError,
  setGuaranteeRequestPayload,
  startCreatePayloadLoading
} from 'redux/slices/create-payload'
import { setError, setNoError } from 'redux/slices/createError'
import { workflowError, clearWorkflowError, workflowSuccess } from 'redux/slices/workflowError'
import { getAllLegalEntitiesSuccess, hasError } from 'redux/slices/legalEntities'
import { getAllLegals } from 'redux/slices/legals'
import { appendNotificationSuccess, getAllNotificationsSuccess } from 'redux/slices/notifications'
import { getPdf, clearPdf } from 'redux/slices/pdf'
import { getAllPurposeTypesSuccess } from 'redux/slices/purposeTypes'
import {
  getSponsoredLegalEntitiesSuccess,
  hasSponsoredEntityError
} from 'redux/slices/sponsored-private-entity'
import { getAllTermsConditionsSuccess } from 'redux/slices/terms'
import { getAllAdminCreateActions } from 'redux/slices/admin-create-actions'
import { getUserDetails } from 'redux/slices/user'
import fetchAndDispatch from 'utils/fetchandDispatch'
import {
  appendAdminOrgDetails,
  editAdminOrgDetails,
  getAllAdminOrgDetails
} from 'redux/slices/admin-org-details'
import { format, parseISO } from 'date-fns'
import { amendExistingRoles, appendToRoleDetails, getAllAdminRoles } from 'redux/slices/admin-roles'
import { getAllAdminEntities } from 'redux/slices/admin-le'

// ----------------------------------------------------------------------

export function eventCentral(data: string) {
  const { dispatch } = store

  try {
    const parsedData: Response = JSON.parse(data)
    
    const {
      Status,
      QueryContractResponse,
      QueryDocumentResponse,
      QueryCreateActionsResponse,
      QueryContractHistoryResponse,
      ContractEvent,
      WorkflowEvent,
      QueryNotificationsResponse,
      NotificationEvent,
      QueryLegalEntitiesResponse,
      QueryPurposeTypesResponse,
      QueryAvailableContractTermsResponse,
      AuthConfirmation,
      QueryContractDocumentResponse,
      QueryLegalDocumentListResponse,
      AddPrivatePartyResponse,
      /*@ts-ignore*/
      AdminQueryUsersResponse,
      AdminQueryCreateActionsResponse,
      /*@ts-ignore*/
      AdminQueryOrgUnitsResponse,
      /*@ts-ignore*/
      AdminQueryRolesResponse,
      /*@ts-ignore*/
      AdminQueryLegalEntitiesResponse,
      UIAdminWorkflowEvent
    } = parsedData
    switch (parsedData.messageType) {
      case 'Status':
        if (!!Status && Status.status !== 'OK') {
          if (Status.statusCode === 9098) {
            //Private Entity Creation
            dispatch(hasSponsoredEntityError(Status))
            break
          }
          // Dispatch workflow error which handles all state updates atomically
          dispatch(workflowError({
            statusCode: Status.statusCode,
            status: Status.status,
            description: Status.description || 'An error occurred while processing your request.'
          }))
          // Also set error for backward compatibility with ErrorNotificationListener
          dispatch(setError({
            ...parsedData?.Status,
            description: Status.description || 'An error occurred while processing your request.'
          }))
          // Clear loading states and related data
          dispatch(stopGetGuaranteeLoading())
          dispatch(clearGuaranteeRequest())
          dispatch(clearContractHistory())
          dispatch(clearPdf())
          break
        }
        // Success case - clear any workflow errors and stop loading
        dispatch(workflowSuccess())
        dispatch(setNoError())
        dispatch(stopGetGuaranteeLoading())
        break
      case 'ApplicationStatus':
        // Nothing to do
        break

      case 'ContractEvent':
        dispatch(startGetGuaranteeLoading())
        if (ContractEvent?.TriContractPaperRecord) {
          dispatch(appendPaperGuaranteeSuccess(ContractEvent))
        } else {
          dispatch(appendGuaranteeSuccess(ContractEvent))
        }
        dispatch(getGuaranteeRequestSuccess(ContractEvent))
        break
      case 'WorkflowEvent':
        const workFlowEvents = ['CANCELLED', 'APPROVED', 'SUBMITTED', 'FAILED', 'UPDATED']
        let dataObj
        if (WorkflowEvent?.contractType !== ContractType.TriContract) {
          const workflowObject =
            WorkflowEvent?.businessUnitActionContext?.[0]?.availableActions?.[0]
          const actionTypeReceived =
            WorkflowEvent?.businessUnitActionContext?.[0]?.availableActions?.[0]
              ?.bffTripartiteAction?.actionType
          const obj =
            WorkflowEvent?.businessUnitActionContext?.[0]?.availableActions?.[0]
              ?.bffTripartiteAction?.[`${actionTypeReceived}`]
          const dataObj = {
            contractType: ContractType.TriContractPaperRecord,
            TriContractState: {
              eventTimestamp: WorkflowEvent?.eventTimestamp,
              contractIdentifier: WorkflowEvent?.contractIdentifier,
              partyGroupRole: WorkflowEvent?.workflowRole?.roleType,
              contractRevision: WorkflowEvent?.contractRevision,
              state: WorkflowEvent?.workflowState,
              stateDisplay: WorkflowEvent?.workflowStateDisplay,
              newExpires: obj?.newExpires,
              commences: obj?.commences,
              expires: obj?.expires,
              applicants: workflowObject?.bffTripartiteAction?.CREATE?.applicants,
              beneficiaries: workflowObject?.bffTripartiteAction?.CREATE?.beneficiaries,
              issuers: workflowObject?.bffTripartiteAction?.CREATE?.issuers,
              amount: workflowObject?.bffTripartiteAction?.CREATE?.amount,
              purpose: workflowObject?.bffTripartiteAction?.CREATE?.purpose,
              purposeData: workflowObject?.bffTripartiteAction?.CREATE?.purposeData,
              terms: {
                termsIdentifier:
                  workflowObject?.bffTripartiteAction?.CREATE?.contractTermsIdentifier
              }
            },
            businessUnitActionContext: WorkflowEvent?.businessUnitActionContext
          }
          if (
            WorkflowEvent?.workflowState &&
            workFlowEvents.includes(WorkflowEvent?.workflowState)
          ) {
            dispatch(appendGuaranteeSuccessForWorkflow(dataObj))
          }
        } else if (
          WorkflowEvent?.action === 'EXECUTE' &&
          WorkflowEvent?.workflowState &&
          workFlowEvents.includes(WorkflowEvent?.workflowState)
        ) {
          dataObj = {
            reference: WorkflowEvent?.reference,
            contractType: 'TriContract',
            TriContractState: {
              eventTimestamp: WorkflowEvent?.eventTimestamp,
              contractIdentifier: WorkflowEvent?.contractIdentifier,
              partyGroupRole: WorkflowEvent?.workflowRole?.roleType,
              contractRevision: WorkflowEvent?.contractRevision,
              state: WorkflowEvent?.workflowState,
              stateDisplay: WorkflowEvent?.workflowStateDisplay
            },
            businessUnitActionContext: WorkflowEvent?.businessUnitActionContext
          }
          dispatch(appendGuaranteeSuccessForWorkflow(dataObj))
          dispatch(getGuaranteeRequestSuccess(dataObj))
        } else {
          const workProposalFlowEvents = ['PROPOSED', 'UPDATED', 'CANCELLED'] // added 'CANCELLED' here to handle the update of revert status
          const workflowStatesForPropsal = ['PROPOSE', 'APPROVE', 'CANCEL']
          if (
            WorkflowEvent?.action &&
            workflowStatesForPropsal.includes(WorkflowEvent?.action) &&
            WorkflowEvent?.workflowState &&
            workProposalFlowEvents.includes(WorkflowEvent?.workflowState)
          ) {
            if (
              WorkflowEvent?.businessUnitActionContext?.[0]?.availableActions?.[0]
                ?.bffTripartiteAction?.actionType === 'CREATE'
            ) {
              const workflowObject =
                WorkflowEvent?.businessUnitActionContext?.[0]?.availableActions?.[0]
              const actionTypeReceived =
                WorkflowEvent?.businessUnitActionContext?.[0]?.availableActions?.[0]
                  ?.bffTripartiteAction?.actionType
              const obj =
                WorkflowEvent?.businessUnitActionContext?.[0]?.availableActions?.[0]
                  ?.bffTripartiteAction?.[`${actionTypeReceived}`]
              const dataObj = {
                contractType: 'TriContract',
                TriContractState: {
                  eventTimestamp: WorkflowEvent?.eventTimestamp,
                  contractIdentifier: WorkflowEvent?.contractIdentifier,
                  partyGroupRole: WorkflowEvent?.workflowRole?.roleType,
                  contractRevision: WorkflowEvent?.contractRevision,
                  state: WorkflowEvent?.workflowState,
                  stateDisplay: WorkflowEvent?.workflowStateDisplay,
                  newExpires: obj?.expires,
                  commences: obj?.commences,
                  expires: obj?.expires,
                  applicants: workflowObject?.bffTripartiteAction?.CREATE?.applicants,
                  beneficiaries: workflowObject?.bffTripartiteAction?.CREATE?.beneficiaries,
                  issuers: workflowObject?.bffTripartiteAction?.CREATE?.issuers,
                  amount: workflowObject?.bffTripartiteAction?.CREATE?.amount,
                  purpose: workflowObject?.bffTripartiteAction?.CREATE?.purpose,
                  purposeData: workflowObject?.bffTripartiteAction?.CREATE?.purposeData,
                  terms: {
                    termsIdentifier:
                      workflowObject?.bffTripartiteAction?.CREATE?.contractTermsIdentifier
                  }
                },
                businessUnitActionContext: WorkflowEvent?.businessUnitActionContext
              }
              const workFlowEvents = [
                'CANCELLED',
                'APPROVED',
                'SUBMITTED',
                'FAILED',
                'UPDATED',
                'PROPOSED'
              ]
              if (
                WorkflowEvent?.workflowState &&
                workFlowEvents.includes(WorkflowEvent?.workflowState)
              ) {
                dispatch(appendGuaranteeSuccessForWorkflow(dataObj))
                dispatch(getGuaranteeRequestSuccess(dataObj))
              }
            } else {
              dataObj = {
                reference: WorkflowEvent?.reference,
                contractType: 'TriContract',
                TriContractState: {
                  eventTimestamp: WorkflowEvent?.eventTimestamp,
                  contractIdentifier: WorkflowEvent?.contractIdentifier,
                  partyGroupRole: WorkflowEvent?.workflowRole?.roleType,
                  contractRevision: WorkflowEvent?.contractRevision
                },
                businessUnitActionContext: WorkflowEvent?.businessUnitActionContext
              }
              dispatch(appendGuaranteeSuccessForWorkflow(dataObj))
              dispatch(getGuaranteeRequestSuccess(dataObj))
            }
          }
        }
        break

      case 'QueryLegalEntitiesResponse':
        if (QueryLegalEntitiesResponse) {
          const hasEntries = QueryLegalEntitiesResponse.entries

          if (hasEntries && hasEntries?.length > 0) {
            dispatch(
              getAllLegalEntitiesSuccess({
                entities: QueryLegalEntitiesResponse?.entries as LegalEntityParty[],
                Status: Status ?? {},
                requestId: parsedData?.messageIdReference
              })
            )
          } else {
            dispatch(
              hasError({
                Status: Status ?? { description: 'Unknown Error' },
                requestId: parsedData?.messageIdReference
              })
            )
          }
        }
        break;

      case 'AdminQueryLegalEntitiesResponse':
        dispatch(getAllAdminEntities(AdminQueryLegalEntitiesResponse?.leList))
        break

      case 'QueryAvailableContractTermsResponse':
        dispatch(getAllTermsConditionsSuccess(QueryAvailableContractTermsResponse))
        break

      case 'UIAdminWorkflowEvent':
        const keyValue = UIAdminWorkflowEvent?.domainEntityType
        const cleanDateTime = UIAdminWorkflowEvent?.eventTimestamp
          ? UIAdminWorkflowEvent.eventTimestamp.split('[')[0]
          : ''

        // Step 2: Parse the ISO string into a Date object
        const parsedDate = parseISO(cleanDateTime)

        // Step 3: Format the Date object into the desired format
        const formattedDateTime = format(parsedDate, 'yyyy-MM-dd HH:mm:ss.SSSSSS')
        let actionType
        let state
        let uiWorkflowEvent
        const statuses = ['ACTIVATE', 'DEACTIVATE']
        switch (keyValue) {
          case 'ORGUNITADMIN':
            /*@ts-ignore*/
            actionType = UIAdminWorkflowEvent?.orgUnitAdminDomainAction?.actionType
            state = UIAdminWorkflowEvent?.action
            if (actionType === 'AMEND' && state === 'COMPLETE') {
              uiWorkflowEvent = {
                individualIdentifier: UIAdminWorkflowEvent?.domainEntityIdentifier,

                commonName:
                  /*@ts-ignore*/
                  UIAdminWorkflowEvent?.orgUnitAdminDomainAction?.OrgUnitAdminData?.commonName
              }
              dispatch(editAdminOrgDetails(uiWorkflowEvent))
            } else if (actionType === 'CREATE' && state === 'COMPLETE') {
              uiWorkflowEvent = {
                businessUnitIdentifier: UIAdminWorkflowEvent?.domainEntityIdentifier,
                commonName:
                  /*@ts-ignore*/
                  UIAdminWorkflowEvent?.orgUnitAdminDomainAction?.OrgUnitAdminData?.commonName,

                orgUnitType:
                  /*@ts-ignore*/
                  UIAdminWorkflowEvent?.orgUnitAdminDomainAction?.OrgUnitAdminData?.orgUnitType,
                lastModified: formattedDateTime,
                /*@ts-ignore*/
                leList: UIAdminWorkflowEvent?.OrgUnitAdminData?.leList
              }
              dispatch(appendAdminOrgDetails(uiWorkflowEvent))
            }
            break
          case 'ROLEADMIN':
            /*@ts-ignore*/
            actionType = UIAdminWorkflowEvent?.roleAdminDomainAction?.actionType
            state = UIAdminWorkflowEvent?.action
            if (actionType === 'AMEND' && state === 'COMPLETE') {
              uiWorkflowEvent = {
                /*@ts-ignore*/
                roleIdentifier: UIAdminWorkflowEvent?.domainEntityIdentifier,
                /*@ts-ignore*/
                commonName: UIAdminWorkflowEvent?.roleAdminDomainAction?.RoleAdminData?.commonName
              }
              dispatch(amendExistingRoles(uiWorkflowEvent))
            } else if (actionType === 'CREATE' && state === 'COMPLETE') {
              uiWorkflowEvent = {
                roleIdentifier:
                  /*@ts-ignore*/
                  UIAdminWorkflowEvent?.domainEntityIdentifier,
                /*@ts-ignore*/
                roleType: UIAdminWorkflowEvent?.roleAdminDomainAction?.RoleAdminData?.roleType,
                /*@ts-ignore*/
                orgUnitIdentifier: UIAdminWorkflowEvent?.roleAdminDomainAction?.RoleAdminData?.orgUnitIdentifier,
                /*@ts-ignore*/
                individualIdentifier: UIAdminWorkflowEvent?.roleAdminDomainAction?.RoleAdminData?.individualIdentifier,
                lastModified: formattedDateTime,
                /*@ts-ignore*/
                commonName:
                  /*@ts-ignore*/
                  UIAdminWorkflowEvent?.roleAdminDomainAction?.RoleAdminData?.commonName
              }
              dispatch(appendToRoleDetails(uiWorkflowEvent))
            }
            break

          case 'USERADMIN':
            actionType = UIAdminWorkflowEvent?.userAdminDomainAction?.actionType
            state = UIAdminWorkflowEvent?.action
            const roles = UIAdminWorkflowEvent?.userAdminDomainAction?.UserAdminData?.roles ? { roles: UIAdminWorkflowEvent?.userAdminDomainAction?.UserAdminData?.roles } : []
            if (statuses.includes(actionType) && state === 'COMPLETE') {
              const email = UIAdminWorkflowEvent?.userAdminDomainAction?.UserAdminData?.email ? { email: UIAdminWorkflowEvent?.userAdminDomainAction?.UserAdminData?.email } : {}
              uiWorkflowEvent = {
                individualIdentifier: UIAdminWorkflowEvent?.domainEntityIdentifier,
                ...email,
                ...roles,
                activeStatus:
                  UIAdminWorkflowEvent?.userAdminDomainAction?.actionType
              }
              dispatch(amendUserDetails(uiWorkflowEvent))
            } else if (actionType === 'CREATE' && state === 'COMPLETE') {
              uiWorkflowEvent = {
                individualIdentifier: UIAdminWorkflowEvent?.domainEntityIdentifier,
                ...UIAdminWorkflowEvent?.userAdminDomainAction?.UserAdminData,
                roles: [],
                roleDescriptions: "NONE"
              }
              dispatch(addNewUserDetails(uiWorkflowEvent))
            } else if (actionType === 'AMEND' && state === 'COMPLETE') {
              uiWorkflowEvent = {
                individualIdentifier: UIAdminWorkflowEvent?.domainEntityIdentifier,
                ...UIAdminWorkflowEvent?.userAdminDomainAction?.UserAdminData,
              }
              dispatch(amendUserDetails(uiWorkflowEvent))
            }
            break

          default:
            break
        }
        break

      case 'QueryContractResponse':
        fetchAndDispatch(QueryContractResponse, dispatch)
        break

      case 'QueryDocumentResponse':
        if (QueryDocumentResponse) {
          window.sessionStorage.setItem('pdf', QueryDocumentResponse?.data ?? '')
          dispatch(getPdf(QueryDocumentResponse?.data))
        }
        break

      case 'QueryContractDocumentResponse':
        if (QueryContractDocumentResponse) {
          window.sessionStorage.setItem('contractPdf', QueryContractDocumentResponse?.data ?? '')
          dispatch(getContractPdf(QueryContractDocumentResponse?.data))
        }
        break

      case 'QueryPurposeTypesResponse':
        const entries = QueryPurposeTypesResponse?.entries ?? []
        dispatch(getAllPurposeTypesSuccess(entries))
        break

      case 'QueryContractHistoryResponse':
        dispatch(setHistory(QueryContractHistoryResponse))
        break

      case 'AdminQueryCreateActionsResponse':
        /*@ts-ignore*/
        dispatch(getAllAdminCreateActions(AdminQueryCreateActionsResponse?.entries))
        break

      case 'AdminQueryUsersResponse':
        /*@ts-ignore*/
        dispatch(getAllAdminUsers(AdminQueryUsersResponse?.userList))
        break

      case 'AdminQueryOrgUnitsResponse':
        /*@ts-ignore*/
        dispatch(getAllAdminOrgDetails(AdminQueryOrgUnitsResponse?.orgUnitList))
        break

      /*@ts-ignore*/
      case 'AdminQueryRolesResponse':
        dispatch(getAllAdminRoles(AdminQueryRolesResponse.roleList))
        break

      case 'QueryNotificationsResponse':
        dispatch(getAllNotificationsSuccess(QueryNotificationsResponse?.entries))
        break

      case 'AddPrivatePartyResponse':
        dispatch(getSponsoredLegalEntitiesSuccess(AddPrivatePartyResponse?.party))

        break
      case 'NotificationEvent':
        NotificationEvent && dispatch(appendNotificationSuccess(NotificationEvent))
        break

      case 'AuthConfirmation':
        dispatch(getUserDetails(AuthConfirmation))
        break

      case 'QueryCreateActionsResponse':
        dispatch(startCreatePayloadLoading())
        if (Status?.status === 'OK') {
          // Handle case where QueryCreateActionsResponse is empty object or doesn't have entries
          const entries = QueryCreateActionsResponse?.entries ?? []
          dispatch(setGuaranteeRequestPayload(entries))
        } else {
          dispatch(hasCreatePayloadError('Error Occurred while fetching create Payload'))
        }

        break

      case 'QueryLegalDocumentListResponse':
        dispatch(getAllLegals(QueryLegalDocumentListResponse?.legalDocumentList))
        break

      default:
        break
    }
  } catch (error) {
    // Error caught but not logged
  }
}
